import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import windowService from '../../../services/windowService';
import { useUser } from '../../authentication';
import { getUserOrgs, switchOrg } from '../../org';

type UrlParams = {
  trainingId: string;
};

export type Org = {
  _id: {
    id: string;
  };
  name: string;
  memberRoles: {
    permission: {
      permissions: {
        access: string;
      }[];
    };
  }[];
  members?: Array<unknown>;
};

type State = {
  isLoading: boolean;
  isError: boolean;
  isSubmitting: boolean;
};

export const useSelectOrgForChurchCares = () => {
  const { user, refreshUser } = useUser();
  const { trainingId } = useParams<UrlParams>();
  const [status, setStatus] = useState<State>({
    isLoading: true,
    isError: false,
    isSubmitting: false,
  });
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>();

  const hasOrgRolePermission = (perm: string, org: Org) => {
    return org.memberRoles.some(role => role.permission.permissions.some(curPerm => curPerm.access === perm));
  };

  const redirectBacktoOnboarding = () => {
    windowService.redirectTo(`/onboarding/churchcares`);
  };

  const redirectToDashboard = () => {
    windowService.redirectTo(`/dashboard`);
  };

  const redirectToAssignTraining = () => {
    windowService.redirectTo(`/assign-training/${trainingId}`);
  };

  const selectedOrg = orgs.find(org => org._id.id === selectedOrgId);
  const hasAssignViewPermission = selectedOrg ? hasOrgRolePermission('Assign View', selectedOrg) : false;

  const submitOrgChange = () => {
    setStatus(prevStatus => ({ ...prevStatus, isSubmitting: true }));

    if (selectedOrgId === user!.lastSelectedAccount) {
      redirectToAssignTraining();
      return;
    }

    switchOrg(user!.userId, selectedOrgId!)
      .then(() => refreshUser())
      .then(() => redirectToAssignTraining())
      .catch(reason => {
        console.error(reason);
        setStatus(prevStatus => ({ ...prevStatus, isSubmitting: false, isError: true }));
      });
  };

  useEffect(() => {
    if (!user) {
      setStatus(prevStatus => ({ ...prevStatus, isLoading: false, isError: true }));
      return;
    }

    getUserOrgs()
      .then(loadedOrgs => {
        setOrgs(loadedOrgs);
        setSelectedOrgId(user.lastSelectedAccount);
        setStatus(prevStatus => ({ ...prevStatus, isLoading: false }));
      })
      .catch(reason => {
        console.error(reason);
        setStatus(prevStatus => ({ ...prevStatus, isLoading: false, isError: true }));
      });
  }, []);

  return {
    isLoading: status.isLoading,
    isError: status.isError,
    isSubmitting: status.isSubmitting,
    orgs,
    selectedOrgId,
    setSelectedOrgId,
    submitOrgChange,
    redirectBacktoOnboarding,
    redirectToDashboard,
    hasAssignViewPermission,
  };
};
